import { styled } from '@linaria/react';

import type { ALIGNS, FONT_WEIGHTS, TRANSFORMS } from './constants';

export type TAlign = (typeof ALIGNS)[number];
export type TTextTransform = (typeof TRANSFORMS)[number];
export type TFontWeight = (typeof FONT_WEIGHTS)[number];

export const H1 = styled.h1<{
	align?: TAlign;
	transform?: TTextTransform;
	weight?: TFontWeight;
	multiline?: boolean;
}>`
	font-size: 32px;
	line-height: 44px;
	font-weight: ${({ weight }) => weight ?? 400};
	text-align: ${({ align }) => align ?? 'center'};
	text-wrap: balance;
	text-transform: ${({ transform }) => transform ?? 'inherit'};
	white-space: ${({ multiline }) => (multiline ? 'pre-wrap' : 'inherit')};
`;

export const H2 = styled.h2<{
	align?: TAlign;
	transform?: TTextTransform;
	weight?: TFontWeight;
	multiline?: boolean;
}>`
	font-size: 21px;
	line-height: 25px;
	font-weight: ${({ weight }) => weight ?? 500};
	text-align: ${({ align }) => align ?? 'center'};
	text-wrap: balance;
	text-transform: ${({ transform }) => transform ?? 'inherit'};
	white-space: ${({ multiline }) => (multiline ? 'pre-wrap' : 'inherit')};
`;

export const H3 = styled.h3<{
	align?: TAlign;
	transform?: TTextTransform;
}>`
	font-size: 18px;
	line-height: 28px;
	font-weight: 700;
	text-align: ${({ align }) => align ?? 'center'};
	text-wrap: balance;
	text-transform: ${({ transform }) => transform ?? 'inherit'};
`;

export const paragraphSizes = {
	xxsmall: {
		fz: 10,
		lh: 12.5,
	},
	xsmall: {
		fz: 12,
		lh: 14,
	},
	small: {
		fz: 14,
		lh: 18,
	},
	medium: {
		fz: 16,
		lh: 20,
	},
	large: {
		fz: 18,
		lh: 24,
	},
	xlarge: {
		fz: 21,
		lh: 32,
	},
	xxlarge: {
		fz: 24,
		lh: 36,
	},
};

export type TParagraphSize = keyof typeof paragraphSizes;

export const P = styled.p<{
	align?: TAlign;

	/**
	 * | Size     | Font Size | Line Height |
	 * |----------|----------|-------------|
	 * | xxsmall  | 10       | 12.5        |
	 * | xsmall   | 12       | 14          |
	 * | small    | 14       | 18          |
	 * | medium   | 16       | 20          |
	 * | large    | 18       | 24          |
	 * | xlarge   | 21       | 32          |
	 * | xxlarge  | 24       | 36          |
	 */
	size?: TParagraphSize;
	transparent?: boolean;
	brandedLink?: boolean;
	danger?: boolean;
	multiline?: boolean;
	weight?: TFontWeight;
	transform?: TTextTransform;
	underline?: boolean;
	italic?: boolean;
}>`
	font-size: ${({ size = 'large' }) => `${paragraphSizes[size].fz}px`};
	font-style: ${({ italic }) => (italic ? 'italic' : 'normal')};
	line-height: ${({ size = 'large' }) => `${paragraphSizes[size].lh}px`};
	font-weight: ${({ weight }) => weight ?? 400};
	text-align: ${({ align }) => align ?? 'center'};
	white-space: ${({ multiline }) => (multiline ? 'pre-wrap' : 'inherit')};
	color: ${({ transparent, danger }) =>
		transparent
			? 'var(--color-white-05)'
			: danger
			? 'var(--color-error)'
			: 'inherit'};
	text-transform: ${({ transform }) => transform ?? 'inherit'};

	a {
		color: ${({ transparent, brandedLink }) =>
			transparent
				? 'var(--color-white-08)'
				: brandedLink
				? 'var(--brand-pink-light)'
				: 'var(--color-white)'};
		text-decoration: ${({ underline }) => (underline ? 'underline' : 'auto')};
		font-weight: ${({ brandedLink }) => (brandedLink ? 600 : 'inherit')};
		cursor: pointer;
		&:hover {
			color: var(--color-white);
			text-decoration: underline;
		}
	}
`;
